import React, {useEffect, useState} from 'react';
import {SearchIcon, UnsplashHeaderLogo} from '../../ui-components/Iconpack';
import {Button} from '../../ui-components/Button/button';
import {RootState, useAppDispatch} from '../../store';
import {getUnsplashPhotos} from '../../actions/media';
import {useSelector} from 'react-redux';
import {UnsplashPhotos, UnsplashResponse} from '../../reducers/model';
import {Dialog, DialogContent} from 'src/ui-components/Modal';
import {Link} from 'react-router-dom';
import CheckIcon from 'src/assets/icons/check.svg';

interface UnsplashProps {
    modalClosed: boolean;
    onClose: () => void;
    onUnsplashSelection: (selectedImages: UnsplashPhotos[]) => void;
}

const UnsplashImageSearch = ({modalClosed, onClose, onUnsplashSelection}: UnsplashProps) => {
    const dispatch = useAppDispatch();
    const [query, setQuery] = useState('');
    const [unsplashImages, setUnsplashImages] = useState<UnsplashResponse | undefined>();
    const [selectedImages, setSelectedImages] = useState<UnsplashPhotos[]>([]);
    let mediaProps = useSelector((state: RootState) => state.media);
    const defaultState: UnsplashResponse = {results: []};

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            searchUnsplashImages();
        }
    };

    const searchUnsplashImages = () => {
        if (query) {
            setSelectedImages([]);
            dispatch(getUnsplashPhotos({query, limit: 2, page: 1}));
        }
    };

    const toggleImageSelection = (image: UnsplashPhotos) => {
        if (selectedImages.some((selectedImage) => selectedImage.id === image.id)) {
            setSelectedImages(selectedImages.filter((selectedImage) => selectedImage.id !== image.id));
        } else {
            setSelectedImages([...selectedImages, image]);
        }
    };

    useEffect(() => {
        if (mediaProps.unsplashPhotos.results && mediaProps.unsplashPhotos.results.length) {
            setUnsplashImages(mediaProps.unsplashPhotos);
        }
    }, [mediaProps.unsplashPhotos]);

    useEffect(() => {
        setUnsplashImages(defaultState);
        setQuery('');
        setSelectedImages([]);
    }, [modalClosed]);

    useEffect(() => {
        return setUnsplashImages(defaultState);
    }, []);

    return (
        <Dialog open={!modalClosed}>
            <DialogContent
                className='md:h-[570px h-[550px] w-[339px] rounded-[12px] md:w-[904px]'
                onClose={() => {
                    setSelectedImages([]);
                    setQuery('');
                    setUnsplashImages(defaultState);
                    onClose();
                }}>
                <div className='flex flex-col items-center justify-between'>
                    <div className='flex flex-col items-center gap-[20px]'>
                        <UnsplashHeaderLogo />
                        <div
                            className={`flex h-[56px] w-[286px] items-center rounded-[8px] border border-[#D0D5DD] px-6 md:w-[834px]`}>
                            <input
                                type='text'
                                placeholder='Search images on unsplash'
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className='flex-grow text-[1rem] text-[#000000] outline-none placeholder:text-[1rem] placeholder:font-[600] placeholder:text-[#98A2B3]'
                            />
                            <div
                                className={`${query.length > 0 ? 'cursor-pointer' : ''}`}
                                onClick={searchUnsplashImages}>
                                <SearchIcon />
                            </div>
                        </div>
                        {unsplashImages ? (
                            <div
                                className='grid h-[330px] grid-cols-2 gap-[18px] overflow-y-auto md:grid-cols-6'
                                style={{scrollbarWidth: 'none'}}>
                                {unsplashImages.results.map((image) => (
                                    <div key={image.id} className='group'>
                                        <div
                                            key={image.id}
                                            className='relative'
                                            onClick={() => toggleImageSelection(image)}>
                                            <img
                                                alt={image.id}
                                                src={image.urls.regular}
                                                height={194}
                                                width={194}
                                                className={`h-[120px] w-[120px] cursor-pointer rounded-[8px] object-cover ${selectedImages.some((selectedImage) => selectedImage.id === image.id) ? 'border-2 border-blue-500' : ''}`}
                                            />
                                            <img
                                                src={CheckIcon}
                                                alt={'check'}
                                                className={`absolute right-0 top-0 h-6 w-6 ${selectedImages.some((selectedImage) => selectedImage.id === image.id) ? 'block' : 'hidden'}`}
                                            />

                                            <Link
                                                to={`${image.user.links.html}?utm_source=perceptpixel&utm_medium=referral`}
                                                target='_blank'>
                                                <span
                                                    key={image.id}
                                                    className='absolute bottom-0 left-0 my-1 hidden w-full
                                    cursor-pointer bg-black bg-opacity-30 text-center text-[12px] font-[600] text-white hover:underline group-hover:block'>
                                                    {image.user.name}
                                                </span>
                                            </Link>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>
                {selectedImages.length > 0 && (
                    <div className='flex gap-4 md:p-1'>
                        <Button variant='primary' size='lg' onClick={() => onUnsplashSelection(selectedImages)}>
                            Select {selectedImages.length}
                        </Button>
                        <Button
                            variant='secondary'
                            size='lg'
                            onClick={() => {
                                setSelectedImages([]);
                            }}>
                            Cancel
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default UnsplashImageSearch;
